/**
 * PGSS-4051 — narrowing the shipment order list to one delivery address.
 *
 * Staff build a shipment by gathering a customer's orders. A customer can ask for different orders
 * to go to different addresses, and that screen carries a lot of orders, so the address was shown
 * per group but there was no way to act on it.
 *
 * It is not only a speed problem. If a customer's orders span several addresses and staff group
 * them together, the shipment carries orders meant for different places.
 *
 * Kept as pure functions so the behaviour is testable without mounting the shipment page, which
 * needs redux, a router and a permissions context.
 */

/** A group with no ship-to is not "unlabelled" — it is a group this filter cannot speak about. */
const shipToOf = (group) => {
  const value = group?.shipTo;
  return typeof value === 'string' ? value.trim() : '';
};

/**
 * The distinct addresses present in the loaded orders, sorted for a stable dropdown.
 *
 * Derived from what is on screen rather than fetched: the filter narrows what staff are already
 * looking at, so offering an address with no orders behind it would be a dead option.
 */
export const shipToOptions = (customerOrders) => {
  const seen = new Set();
  (customerOrders || []).forEach((group) => {
    const value = shipToOf(group);
    if (value) seen.add(value);
  });
  return [...seen].sort((a, b) => a.localeCompare(b)).map((name) => ({ name }));
};

/**
 * Worth offering at all?
 *
 * Originally this was "two or more addresses": with one address every row already matches, so the
 * control could not narrow anything.
 *
 * QA failed that (PGSS-4051, Mithun): a control that appears and disappears depending on the data
 * is indistinguishable from a broken one. It reads as "the Ship to filter is missing", and the
 * reports that followed -- "only shows after applying Weight", "not shown when I pick a customer",
 * "gone when weight is over 10,000" -- were all the same rule seen from different angles, because
 * each of those changes which orders are loaded and therefore how many addresses are present.
 *
 * So it is shown whenever the loaded orders carry ANY address. With one, it lists that address and
 * clearing it returns to all -- pointless as a filter, but it answers "is this screen missing
 * something?", which turned out to matter more.
 *
 * The second failed round added the other half of the rule: it takes a CUSTOMER as well.
 *
 * The screen loads every customer's orders until one is chosen, so "any address present" was true
 * on arrival and the control appeared straight away, offering a list of addresses drawn from
 * customers the staff member has not picked -- choosing one would narrow the page to whoever
 * happens to ship there. That is not what "Ship to" means beside a Customer control; it is why the
 * ticket says "beside the customer", and why the agreed behaviour was "before choosing a customer,
 * no control".
 *
 * Still hidden at zero addresses too: orders that carry none leave nothing to list.
 */
export const shouldOfferShipToFilter = (customerOrders, customer) =>
  Boolean(customer) && shipToOptions(customerOrders).length > 0;

/**
 * Apply the filter. No selection returns the list untouched — this is an optional aid, never a gate.
 *
 * Groups with no ship-to are kept out when a filter is active: they are not known to belong to the
 * chosen address, and silently including them would defeat the point.
 */
export const filterByShipTo = (customerOrders, selectedShipTo) => {
  const wanted = typeof selectedShipTo === 'string' ? selectedShipTo.trim() : selectedShipTo?.name?.trim();
  if (!wanted) return customerOrders;
  return (customerOrders || []).filter((group) => shipToOf(group) === wanted);
};

/**
 * The address that is ACTUALLY in force, given what is on screen right now.
 *
 * A stored selection can outlive the list it was chosen from -- switch customer, reload, change
 * weight range, and the orders change underneath it. Two ways that turns into a trap:
 *
 *   - the new list has one address or none, so the control unmounts... while the old selection is
 *     still filtering. An empty list, and no control left to clear it with.
 *   - the new list has several addresses but not the chosen one. Empty list until someone works
 *     out why.
 *
 * Resolving on every render rather than storing the answer means no code path can leave it stuck:
 * a selection that no longer applies simply stops applying. There is nothing to remember to reset,
 * which is the part that was missed the first time.
 */
export const resolveActiveShipTo = (customerOrders, selected, customer) => {
  const wanted = typeof selected === 'string' ? selected.trim() : selected?.name?.trim();
  if (!wanted) return null;
  // Whatever hides the control must also stop the filter -- an applied filter with no control to
  // clear it is the trap. That now includes "no customer chosen", which the page returns to on its
  // own when a weight change empties the customer selection.
  if (!shouldOfferShipToFilter(customerOrders, customer)) return null;
  return shipToOptions(customerOrders).some((option) => option.name === wanted) ? wanted : null;
};
